/**
 * Well-known tool names that the agent can use.
 * This is the single source of truth for all tool names used throughout the agent-sessions package.
 *
 * These include:
 * - Standard SWE-agent tools (bash, edit, view, etc.)
 * - Deprecated/legacy tools (kept for backwards compatibility with old sessions)
 * - Custom MCP tools (handled via the string union in ToolName type)
 */
export const TOOL_NAMES = {
  // File operations
  APPLY_PATCH: 'apply_patch',
  STR_REPLACE_EDITOR: 'str_replace_editor',
  STR_REPLACE: 'str_replace',
  EDIT: 'edit',
  CREATE: 'create',
  VIEW: 'view',
  INSERT: 'insert',
  UNDO_EDIT: 'undo_edit',

  // Shell/Terminal operations
  BASH: 'bash',
  WRITE_BASH: 'write_bash',
  READ_BASH: 'read_bash',
  STOP_BASH: 'stop_bash',
  LIST_BASH: 'list_bash',
  ASYNC_BASH: 'async_bash',
  READ_ASYNC_BASH: 'read_async_bash',
  STOP_ASYNC_BASH: 'stop_async_bash',

  // PowerShell operations
  POWERSHELL: 'powershell',
  WRITE_POWERSHELL: 'write_powershell',
  READ_POWERSHELL: 'read_powershell',
  STOP_POWERSHELL: 'stop_powershell',
  LIST_POWERSHELL: 'list_powershell',

  // Search operations
  GREP: 'grep',
  RG: 'rg',
  GLOB: 'glob',
  SEARCH_CODE_SUBAGENT: 'search_code_subagent',
  WEB_SEARCH: 'web_search',
  GITHUB_MCP_SERVER_WEB_SEARCH: 'github-mcp-server-web_search',
  GITHUB_MCP_SERVER_BING_SEARCH: 'github-mcp-server-bing_search',
  GITHUB_MCP_SERVER_PULL_REQUEST_READ: 'github-mcp-server-pull_request_read',
  WEB_FETCH: 'web_fetch',

  // Git operations
  CREATE_PULL_REQUEST: 'create_pull_request',
  COMMIT_CHANGES: 'commit_changes',
  CODE_REVIEW: 'code_review',
  CODEQL_CHECKER: 'codeql_checker',
  PARALLEL_VALIDATION: 'parallel_validation',

  // Setup operations
  RUN_CUSTOM_SETUP_STEP: 'run_custom_setup_step',
  RUN_SETUP: 'run_setup',

  // Communication/Progress
  THINK: 'think',
  SKILL: 'skill',
  REPORT_PROGRESS: 'report_progress',
  REPORT_INTENT: 'report_intent',
  REPLY_TO_COMMENT: 'reply_to_comment',
  PROPOSE_WORK: 'propose_work',

  // Agent operations
  TASK: 'task',
  TASK_COMPLETE: 'task_complete',

  // Memory operations
  STORE_MEMORY: 'store_memory',
} as const

/**
 * Bash/terminal tools. These should not be grouped together, which is why they don't have group titles.
 */
export const BASH_TOOLS = [
  TOOL_NAMES.BASH,
  TOOL_NAMES.WRITE_BASH,
  TOOL_NAMES.READ_BASH,
  TOOL_NAMES.STOP_BASH,
  TOOL_NAMES.LIST_BASH,
  TOOL_NAMES.ASYNC_BASH,
  TOOL_NAMES.READ_ASYNC_BASH,
  TOOL_NAMES.STOP_ASYNC_BASH,
] as const

/**
 * PowerShell tools. These should not be grouped together, which is why they don't have group titles.
 * They behave the same as bash tools.
 */
export const POWERSHELL_TOOLS = [
  TOOL_NAMES.POWERSHELL,
  TOOL_NAMES.WRITE_POWERSHELL,
  TOOL_NAMES.READ_POWERSHELL,
  TOOL_NAMES.STOP_POWERSHELL,
  TOOL_NAMES.LIST_POWERSHELL,
] as const

/**
 * All shell-related tools (Bash + PowerShell). Used for grouping logic.
 */
export const SHELL_TOOLS = [...BASH_TOOLS, ...POWERSHELL_TOOLS] as const

/**
 * Setup tools. These should be grouped together regardless of specific tool name.
 */
export const SETUP_TOOLS = [TOOL_NAMES.RUN_CUSTOM_SETUP_STEP, TOOL_NAMES.RUN_SETUP] as const

/**
 * Edit tools. These are tools that modify files and should show DiffPreviews expanded when in a group.
 */
export const EDIT_TOOLS = [
  TOOL_NAMES.STR_REPLACE_EDITOR,
  TOOL_NAMES.STR_REPLACE,
  TOOL_NAMES.EDIT,
  TOOL_NAMES.APPLY_PATCH,
] as const

/**
 * Returns a grouping key for a tool, or `null` if the tool should always be standalone.
 *
 * - Shell tools and task → `null` (standalone)
 * - Setup tools → `'setup'` (group together regardless of specific name)
 * - Everything else → the tool name itself (group by same name)
 */
export function getToolGroupingKey(toolName: string): string | null {
  if (SHELL_TOOLS.includes(toolName as (typeof SHELL_TOOLS)[number]) || toolName === TOOL_NAMES.TASK) {
    return null
  }
  if (SETUP_TOOLS.includes(toolName as (typeof SETUP_TOOLS)[number])) {
    return 'setup'
  }
  return toolName
}

/**
 * Derive the union type of all known tool names from the constants.
 * This ensures the type is always in sync with the constants.
 */
export type KnownToolName = (typeof TOOL_NAMES)[keyof typeof TOOL_NAMES]

/**
 * Configuration for how to display each tool when multiple are grouped together.
 * Only tools that commonly appear in groups need entries here.
 * Note: count will always be ≥ 2 since single items are rendered standalone.
 */
type ToolDisplayConfig = {
  getTitle: (count: number) => string
  iconToolName: string
}

export const TOOL_GROUP_DISPLAY_CONFIG: Partial<Record<string, ToolDisplayConfig>> = {
  // File operations
  [TOOL_NAMES.APPLY_PATCH]: {
    getTitle: count => `Apply ${count} patches`,
    iconToolName: TOOL_NAMES.APPLY_PATCH,
  },
  [TOOL_NAMES.STR_REPLACE_EDITOR]: {
    getTitle: count => `Edit ${count} files`,
    iconToolName: TOOL_NAMES.EDIT,
  },
  [TOOL_NAMES.STR_REPLACE]: {
    getTitle: count => `Edit ${count} files`,
    iconToolName: TOOL_NAMES.EDIT,
  },
  [TOOL_NAMES.EDIT]: {
    getTitle: count => `Edit ${count} files`,
    iconToolName: TOOL_NAMES.EDIT,
  },
  [TOOL_NAMES.CREATE]: {
    getTitle: count => `Create ${count} files`,
    iconToolName: TOOL_NAMES.CREATE,
  },
  [TOOL_NAMES.VIEW]: {
    getTitle: count => `View ${count} files`,
    iconToolName: TOOL_NAMES.VIEW,
  },
  [TOOL_NAMES.INSERT]: {
    getTitle: count => `Insert at ${count} locations`,
    iconToolName: TOOL_NAMES.INSERT,
  },
  [TOOL_NAMES.UNDO_EDIT]: {
    getTitle: count => `Undo ${count} edits`,
    iconToolName: TOOL_NAMES.UNDO_EDIT,
  },

  // Shell/Terminal operations are excluded from grouping - they always appear standalone

  // Search operations
  [TOOL_NAMES.GREP]: {
    getTitle: count => `Search ${count} times`,
    iconToolName: TOOL_NAMES.GREP,
  },
  [TOOL_NAMES.RG]: {
    getTitle: count => `Search ${count} times`,
    iconToolName: TOOL_NAMES.GREP,
  },
  [TOOL_NAMES.GLOB]: {
    getTitle: () => 'Find files',
    iconToolName: TOOL_NAMES.GLOB,
  },
  [TOOL_NAMES.SEARCH_CODE_SUBAGENT]: {
    getTitle: count => `Search code ${count} times`,
    iconToolName: TOOL_NAMES.SEARCH_CODE_SUBAGENT,
  },
  [TOOL_NAMES.WEB_SEARCH]: {
    getTitle: count => `Web search ${count} times`,
    iconToolName: TOOL_NAMES.GREP,
  },
  [TOOL_NAMES.GITHUB_MCP_SERVER_WEB_SEARCH]: {
    getTitle: count => `Web search ${count} times`,
    iconToolName: TOOL_NAMES.GREP,
  },
  [TOOL_NAMES.GITHUB_MCP_SERVER_BING_SEARCH]: {
    getTitle: count => `Web search ${count} times`,
    iconToolName: TOOL_NAMES.GREP,
  },
  [TOOL_NAMES.WEB_FETCH]: {
    getTitle: count => `Fetch ${count} URLs`,
    iconToolName: TOOL_NAMES.WEB_FETCH,
  },

  // Git operations
  [TOOL_NAMES.COMMIT_CHANGES]: {
    getTitle: count => `Commit ${count} changes`,
    iconToolName: TOOL_NAMES.COMMIT_CHANGES,
  },
  [TOOL_NAMES.CODE_REVIEW]: {
    getTitle: count => `Review ${count} changes`,
    iconToolName: TOOL_NAMES.CODE_REVIEW,
  },
  [TOOL_NAMES.PARALLEL_VALIDATION]: {
    getTitle: () => 'Check changes with Copilot code review and CodeQL',
    iconToolName: TOOL_NAMES.PARALLEL_VALIDATION,
  },

  // Setup operations
  [TOOL_NAMES.RUN_CUSTOM_SETUP_STEP]: {
    getTitle: () => 'Setting up environment',
    iconToolName: TOOL_NAMES.RUN_SETUP,
  },
  [TOOL_NAMES.RUN_SETUP]: {
    getTitle: () => 'Setting up environment',
    iconToolName: TOOL_NAMES.RUN_SETUP,
  },

  // Communication/Progress
  [TOOL_NAMES.THINK]: {
    getTitle: count => `Thought ${count} times`,
    iconToolName: TOOL_NAMES.THINK,
  },
  [TOOL_NAMES.SKILL]: {
    getTitle: count => `Activate ${count} skills`,
    iconToolName: TOOL_NAMES.SKILL,
  },
  [TOOL_NAMES.REPORT_PROGRESS]: {
    getTitle: count => `${count} progress updates`,
    iconToolName: TOOL_NAMES.REPORT_PROGRESS,
  },
  [TOOL_NAMES.REPLY_TO_COMMENT]: {
    getTitle: count => `Reply to ${count} comments`,
    iconToolName: TOOL_NAMES.REPLY_TO_COMMENT,
  },
  [TOOL_NAMES.PROPOSE_WORK]: {
    getTitle: () => 'Propose work',
    iconToolName: TOOL_NAMES.PROPOSE_WORK,
  },
  // Agent operations
  [TOOL_NAMES.TASK]: {
    getTitle: count => `Run ${count} subagent tasks`,
    iconToolName: TOOL_NAMES.TASK,
  },

  // Memory operations
  [TOOL_NAMES.STORE_MEMORY]: {
    getTitle: () => 'Store memory',
    iconToolName: TOOL_NAMES.STORE_MEMORY,
  },
}
